import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { EidosConfigService } from '@eidos/services/eidos-config.service';
import { CoreRoute, EidosAuthType, EidosLogSeverity, ICoreApplicationError, SecurityCheckRequestType } from '@eidos/models/core-constant.model';
import { DynamicApiResponse } from '@eidos/models/dynamic-api-response.model';
import { EidosCompany } from '@eidos/models/eidos-company.model';
import { EidosLanguage } from '@eidos/models/eidos-language.model';
import { EidosMenu } from '@eidos/models/eidos-menu.model';
import { EidosObjectApiResponse, EidosObjectDataApiResponse } from '@eidos/models/eidos-object.model';
import { LoginResponse } from '@eidos/models/login-response.model';
import { IEidosResetCredentialsRequest } from '@eidos/models/mybiz.interface';
import { MyBizApiResponse, MyBizLabels, MyBizVersionApiResponse } from '@eidos/models/mybiz.model';
import { CoreRegisterAccountConfig, CoreRegisterAccountParameters, CoreRegisterAccountResult } from '@eidos/models/core-register-account-config.model';
import { CoreFormatService } from './core-format.service';
import { EidosBaseApiService } from './eidos-base-api.service';
import { EidosLogService } from './eidos-log.service';
import { EidosSecurityService } from './eidos-security.service';

@Injectable({
  providedIn: 'root'
})
export class EidosApiService extends EidosBaseApiService {

  /**
   * Current authentication type
   *
   * @private
   * @memberof EidosApiService
   */
  private authType = EidosAuthType.Form;
  /**
   * Windows auth API base url
   *
   * @private
   * @type {(string | undefined)}
   * @memberof EidosApiService
   */
  private windowsAuthUrl: string | undefined;
  /**
   * Microsoft auth API url
   *
   * @private
   * @type {(string | undefined)}
   * @memberof EidosApiService
   */
  private microsoftAuthUrl = '';

  /**
   * Google auth API url
   *
   * @private
   * @type {(string | undefined)}
   * @memberof EidosApiService
   */
  private googleAuthUrl = '';

  constructor(
    public override coreFormatService: CoreFormatService,
    public override configService: EidosConfigService,
    public override eidosSecurityService: EidosSecurityService,
    public override http: HttpClient,
    public override eidosLogService: EidosLogService,
    public override dialog: MatDialog
  ) {
    super(coreFormatService, configService, eidosLogService, dialog, eidosSecurityService, http);

    this.configService.currentConfig.subscribe(config => {
      this.baseUrl = config.baseUrl;
      this.urlAPI = config.urlAPI;      
      this.windowsAuthUrl = config.windowsAuth?.authorizationUrl;
      this.microsoftAuthUrl = config.microsoftAuth?.authorizationUrl ?? '';
      this.googleAuthUrl = config.googleAuth?.authorizationUrl ?? '';
    });

    this.eidosSecurityService.checkRequest.subscribe(
      check => {
        switch (check) {
          case SecurityCheckRequestType.User:
            this.getUserInfo().subscribe(res => this.eidosLogService.logDebug(EidosLogSeverity.Log, res));
            break;
          case SecurityCheckRequestType.Authentication:
            this.amIAuthenticated().subscribe(res => this.eidosLogService.logDebug(EidosLogSeverity.Log, "Authentication check required. Result:" + res));
            break;
        }
      });
  }

  /**
    * Call APIs to log a client side error
   *
   * @template T
   * @param {T} error
   * @return {*}
   * @memberof EidosApiService
   */
  public logClientSideError<T extends Error>(error: T) {

    const other: any = Object.assign({}, error);

    if (!!other.name) {
      delete other.name;
    }

    if (!!other.message) {
      delete other.message;
    }

    if (!!other.stack) {
      delete other.stack;
    }

    const clientSideError: ICoreApplicationError = {
      message: error.message,
      stack: error.stack ?? error.message,
      username: this.eidosSecurityService.currentLoggedUser.getValue().username,
      info: JSON.stringify(other),
      path: ""
    };

    return this.sendCommand("POST", "general/log", clientSideError)
      .pipe(
        map<HttpResponse<any>, boolean>(
          response => {
            return !!response;
          }),
        catchError(
          (
            error: HttpErrorResponse) => {
            return this.handleGenericError(error);
          }
        )
      );
  }

  /**
    * Call APIs to log into Eidos
    *
    * @param {EidosAuthType} authType Authentication type
    * @param {string} [username]
    * @param {string} [password]
    * @return {*}  {Observable<LoginResponse>}
    * @memberof EidosApiService
    */
  public login(authType: EidosAuthType, username?: string, password?: string): Observable<LoginResponse | MyBizApiResponse> {

    this.authType = authType;

    switch (this.authType) {
      case EidosAuthType.Form:
        return this.sendCommand("POST", "account/login", { username: username, password: password })
          .pipe(
            map<HttpResponse<any>, MyBizApiResponse>(
              response => {
                return new MyBizApiResponse(response);
              }),
            map<MyBizApiResponse, LoginResponse>(

              response => {
                let loginResponse = new LoginResponse(response);
                if (loginResponse.isOk()) {
                  loginResponse.landingPage = this.config?.landingPage ?? CoreRoute.Home;
                  this.eidosSecurityService.setToken(response.data);
                }
                return loginResponse;
              }),
            switchMap(response => {
              if (response.isOk()) return this.getUserInfo();
              return of(response);
            }),
            catchError(
              (
                error: HttpErrorResponse) => {
                return this.handleGenericError(error);
              }
            )
          );
      case EidosAuthType.Windows:
        return this.sendCommand("POST", "account/login", { username: null, password: null }, this.windowsAuthUrl, true)
          .pipe(
            map<HttpResponse<any>, MyBizApiResponse>(
              response => {
                return new MyBizApiResponse(response);
              }),
            map<MyBizApiResponse, LoginResponse>(
              response => {
                let loginResponse = new LoginResponse(response);
                if (loginResponse.isOk()) {
                  loginResponse.landingPage = this.config?.landingPage ?? CoreRoute.Home;
                  this.eidosSecurityService.setToken(response.data);
                }
                return loginResponse;
              }),
            switchMap(response => {
              if (response.isOk()) return this.getUserInfo();
              return of(response);
            }),
            catchError(
              (
                error: HttpErrorResponse) => {
                return this.handleGenericError(error);
              }
            )
          );
      case EidosAuthType.Microsoft:
        return this.loginOauth('microsoftid', this.microsoftAuthUrl);
      case EidosAuthType.Google:
        return this.loginOauth('google', this.googleAuthUrl);
    }
  }

  /**
    * Call APIs to log into Eidos by OAuth
    *
    * @param {string} [provider] provider
    * @return {*}  {Observable<LoginResponse>}
    * @memberof EidosApiService
    */
  loginOauth(provider: string, url: string): Observable<any> {
    const endpoint = (url === '' ? 'general/oauth/' : url) + provider
    return this.sendCommand("GET", endpoint, {}).pipe(
      map<any, MyBizApiResponse>(
        response => {
          return new MyBizApiResponse(response);
        }),
      catchError(
        (
          error: HttpErrorResponse) => {
          return this.handleGenericError(error);
        }
      )
    );
  }
  /**
   * Call APIs to retrieve info of current logged user
   *
   * @return {*}
   * @memberof EidosApiService
   */
  public getUserInfo() {
    switch (this.authType) {
      case EidosAuthType.Form:
        return this.sendCommand("GET", "account/getuserinfo")
          .pipe(
            map<any, MyBizApiResponse>(
              response => {
                return new MyBizApiResponse(response);
              }),
            map<MyBizApiResponse, LoginResponse>(
              response => {
                let loginResponse = new LoginResponse(response);

                if (loginResponse.isOk()) {
                  loginResponse.landingPage = this.config?.landingPage ?? CoreRoute.Home;
                  this.eidosSecurityService.setRawUser(response.data);
                }
                return loginResponse;
              }),
            catchError(
              (
                error: HttpErrorResponse) => {
                return this.handleGenericError(error);
              }
            )
          );
      case EidosAuthType.Windows:
        return this.sendCommand("GET", "account/getuserinfo", undefined, this.windowsAuthUrl, true)
          .pipe(
            map<any, MyBizApiResponse>(
              response => {
                return new MyBizApiResponse(response);
              }),
            map<MyBizApiResponse, LoginResponse>(
              response => {
                let loginResponse = new LoginResponse(response);

                if (loginResponse.isOk()) {
                  loginResponse.landingPage = this.config?.landingPage ?? CoreRoute.Home;
                  this.eidosSecurityService.setRawUser(response.data);
                }
                return loginResponse;
              }),
            catchError(
              (
                error: HttpErrorResponse) => {
                return this.handleGenericError(error);
              }
            )
          );
      default:
        return throwError(() => "Auth not implemented");
    }
  }

  /**
   * Call APIs to check for user autentication
   *
   * @return {*}
   * @memberof EidosApiService
   */
  public amIAuthenticated(): Observable<boolean> {

    switch (this.authType) {
      case EidosAuthType.Windows:
        return this.sendCommand("GET", "account/amiauthenticated", undefined, this.windowsAuthUrl, true)
          .pipe(
            map<any, MyBizApiResponse>(response => new MyBizApiResponse(response)),
            map<MyBizApiResponse, boolean>(
              response => {
                if (response.data.authenticated !== false && typeof (response.data.authenticated) !== "undefined") {
                  this.eidosSecurityService.authenticated.next(true);
                  return true;
                } else {
                  this.eidosSecurityService.clearAuthentication();
                  return false;
                }
              }),
            catchError((error: HttpErrorResponse) => this.handleGenericError(error))
          );
      default:
        return this.sendCommand("GET", "account/amiauthenticated")
          .pipe(
            map<any, MyBizApiResponse>(response => new MyBizApiResponse(response)),
            map<MyBizApiResponse, boolean>(
              response => {
                if (response.data.authenticated !== false && typeof (response.data.authenticated) !== "undefined") {
                  this.eidosSecurityService.authenticated.next(true);
                  return true;
                } else {
                  this.eidosSecurityService.clearAuthentication();
                  return false;
                }
              }),
            catchError((error: HttpErrorResponse) => this.handleGenericError(error))
          );
    }
  }

  /**
   * Call APIs to log out from Eidos
   *
   * @return {*}
   * @memberof EidosApiService
   */
  public logout() {
    return this.sendCommand("GET", "account/logout")
      .pipe(
        map<HttpResponse<any>, MyBizApiResponse>(
          response => {
            return new MyBizApiResponse(response);
          }),
        map<MyBizApiResponse, LoginResponse>(
          response => {
            return new LoginResponse(response);
          }),
        catchError(
          (
            error: HttpErrorResponse) => {
            return this.handleGenericError(error);
          }
        )
      );
  }

  mbIsValid(val: any): boolean {
    return !(typeof val === "undefined" || val === null);
  }

  /**
   * API call to retrieve a generic object
   *
   * @param {number} objectId
   * @return {*}  {Observable<EidosObjectApiResponse>}
   * @memberof EidosApiService
   */
  getObject(objectId: number): Observable<EidosObjectApiResponse> {
    return this.sendCommand("GET", "eobject/get/" + objectId)
      .pipe(
        map<MyBizApiResponse, EidosObjectApiResponse>(
          response => new EidosObjectApiResponse(response)),
        catchError(
          (
            error: HttpErrorResponse) => {
            return this.handleGenericError(error);
          }
        )
      );
  }
  /**
   * API call to retrieve MyBiz version
   *
   * @return {*}  {Observable<EidosMyBizVersionApiResponse>}
   * @memberof EidosApiService
   */
  getMyBizVersion(): Observable<MyBizVersionApiResponse> {
    return this.sendCommand("GET", "eobject/version")
      .pipe(
        map<MyBizApiResponse, MyBizVersionApiResponse>(
          response => new MyBizVersionApiResponse(response)),
        catchError(
          (
            error: HttpErrorResponse) => {
            return this.handleGenericError(error);
          }
        )
      );
  }
  /**
   * API call to retrieve data of a generic MyBiz object
   *
   * @param {number} objectId
   * @param {*} [dataRequest]
   * @return {*}  {Observable<EidosObjectDataApiResponse>}
   * @memberof EidosApiService
   */
  getDataObject(objectId: number, dataRequest?: any): Observable<EidosObjectDataApiResponse> {
    let dataPayload = { obejctId: objectId };
    const payload = dataRequest ? Object.assign(dataPayload, dataRequest) : dataPayload;
    return this.sendCommand("POST", "eobject/getdata/" + objectId, payload)
      .pipe(
        map<any, MyBizApiResponse>(
          response => new MyBizApiResponse(response)
        ),
        map<MyBizApiResponse, EidosObjectDataApiResponse>(
          response => new EidosObjectDataApiResponse(response)),
        catchError(
          (
            error: HttpErrorResponse) => {
            return this.handleGenericError(error);
          }
        )
      );
  }

  getRegisterAccountConfig(): Observable<CoreRegisterAccountConfig> {
    return this.sendCommand("GET", "general/register/account/config", {})
      .pipe(
        map<any, CoreRegisterAccountConfig>(
          response => new CoreRegisterAccountConfig(response)
        ),
        catchError(
          (
            error: HttpErrorResponse) => {
            return this.handleGenericError(error);
          }
        )
      );
  }
  submitRegisterAccount(params: CoreRegisterAccountParameters): Observable<CoreRegisterAccountResult> {
    return this.sendCommand("POST", "general/register/account/save", {})
      .pipe(
        map<any, CoreRegisterAccountResult>(
          response => new CoreRegisterAccountResult(response)
        ),
        catchError(
          (
            error: HttpErrorResponse) => {
            return this.handleGenericError(error);
          }
        )
      );
  }
  /**
   * Get MyBiz icon url for img src
   *
   * @param {string} imageCod
   * @return {*}  {Observable<string>}
   * @memberof EidosApiService
   */
  getMyBizImageUrl(imageCod: string): Observable<string> {
    return of(`${this.urlAPI}/object/immagine/${imageCod}`);
  }

  getMyBizImage(imageCod: string): Observable<string> {
    return this.sendCommand("GET", "object/immagine/" + imageCod)
      .pipe(
        map<HttpResponse<any>, MyBizApiResponse>(
          response => {
            return new MyBizApiResponse(response);
          }),
        map<MyBizApiResponse, any>(
          response => {
            return response.data;
          }),
        catchError(
          (
            error: HttpErrorResponse) => {
            return this.handleGenericError(error);
          }
        )
      );
  }

  loadMenu(): Observable<EidosMenu> {
    return this.sendCommand("GET", "menu/get")
      .pipe(
        map<HttpResponse<any>, MyBizApiResponse>(response => { return new MyBizApiResponse(response); }),
        map<MyBizApiResponse, EidosMenu>(response => { return new EidosMenu(response.data); }),
        catchError((error: HttpErrorResponse) => this.handleGenericError(error))
      );
  }

  getMyBizLabels(): Observable<MyBizLabels> {
    return this.sendCommand("GET", "general/fe-labels")
      .pipe(
        map<HttpResponse<any>, MyBizApiResponse>(response => { return new MyBizApiResponse(response); }),
        map<MyBizApiResponse, MyBizLabels>(response => { return new MyBizLabels(response); }),
        catchError((error: HttpErrorResponse) => this.handleGenericError(error))
      );
  }

  //TODO asap change with dynamic api
  getLabels(): Observable<DynamicApiResponse> {
    const queryUrl = this.getEndPoint('s', 'lables', 'na-list');
    return this.http.get<DynamicApiResponse>(queryUrl, { headers: this.getHeaders(null) })
      .pipe(
        catchError((error: HttpErrorResponse) => this.handleGenericError(error))
      );
  }

  changePassword(oldPassword: string, newPassword: string): Observable<MyBizApiResponse> {
    return this.sendCommand("POST", "account/changepassword", { old_password: oldPassword, new_password: newPassword })
      .pipe(
        map<HttpResponse<any>, MyBizApiResponse>(response => { return new MyBizApiResponse(response); }),
        catchError((error: HttpErrorResponse) => this.handleGenericError(error))
      );
  }

  /**
   * Requests a user credentials reset
   *
   * @param {string} username
   * @return {*}  {Observable<MyBizApiResponse>}
   * @memberof EidosApiService
   */
  resetCredentials(payload: IEidosResetCredentialsRequest): Observable<MyBizApiResponse> {
    payload.resetUrl = this.baseUrl + CoreRoute.ResetCredentials + "/";
    return this.sendCommand("POST", "account/pwd/reset", payload)
      .pipe(
        map<HttpResponse<any>, MyBizApiResponse>(response => { return new MyBizApiResponse(response); }),
        catchError((error: HttpErrorResponse) => this.handleGenericError(error))
      );
  }

  /**
   * Confirms a user credentials reset
   *
   * @param {string} resetId
   * @return {*}  {Observable<MyBizApiResponse>}
   * @memberof EidosApiService
   */
  confirmResetCredentials(resetId: string): Observable<MyBizApiResponse> {
    return this.sendCommand("GET", "account/pwd/reset/" + resetId)
      .pipe(
        map<HttpResponse<any>, MyBizApiResponse>(response => { return new MyBizApiResponse(response); }),
        catchError((error: HttpErrorResponse) => this.handleGenericError(error))
      );
  }

  impersonate(user: string): Observable<MyBizApiResponse> {

    const payload = {
      targetusername: user
    };

    return this.sendCommand("GET", "account/impersonate", payload)
      .pipe(
        map<HttpResponse<any>, MyBizApiResponse>(response => { return new MyBizApiResponse(response); }),
        catchError((error: HttpErrorResponse) => this.handleGenericError(error))
      );
  }
  endImpersonate(): Observable<MyBizApiResponse> {
    return this.sendCommand("GET", "account/endimpersonate")
      .pipe(
        map<HttpResponse<any>, MyBizApiResponse>(response => { return new MyBizApiResponse(response); }),
        catchError(
          (error: HttpErrorResponse) => this.handleGenericError(error))
      );
  }

  //Abbiamo aggiunto d/ e v1/ perché nel momento in cui viene chiamato questo metodo il
  // service coreFormatService non ha ancora gestito il file di configurazione.

  public getCompanies(): Observable<Array<EidosCompany>> {
    return this.sendCommand("GET", "d/v1/company/user").pipe(
      map(res => {
        const result = (res.Body.Companies as any[]).map(c => {
          return new EidosCompany(c);
        });
        return result;
      })
    );
  }
  setCurrentCompany(company: string): Observable<MyBizApiResponse> {
    return this.sendCommand("GET", "account/aziende/" + company)
      .pipe(
        map<HttpResponse<any>, MyBizApiResponse>(response => { return new MyBizApiResponse(response); }),
        catchError((error: HttpErrorResponse) => this.handleGenericError(error))
      );
  }

  getLanguages(): Observable<Array<EidosLanguage>> {
    return this.sendCommand("GET", "account/lingue")
      .pipe(
        map<HttpResponse<any>, MyBizApiResponse>(response => { return new MyBizApiResponse(response); }),
        map<MyBizApiResponse, Array<EidosLanguage>>(response => {
          let languages = Array<EidosLanguage>();
          response.data.forEach((item: any) => languages.push(new EidosLanguage(item)));
          return languages;
        }),
        catchError((error: HttpErrorResponse) => this.handleGenericError(error))
      );
  }

  setCurrentLanguage(codLanguage: string): Observable<MyBizApiResponse> {
    return this.sendCommand("GET", "account/lingue/" + codLanguage)
      .pipe(
        map<HttpResponse<any>, MyBizApiResponse>(response => { return new MyBizApiResponse(response); }),
        catchError((error: HttpErrorResponse) => this.handleGenericError(error))
      );
  }
  /**
   * Calls API to exec a SP and returns data as JSON
   * Requires MyBiz authentication
   *
   * @template T
   * @param {string} JSONName
   * @param {*} [parameters]
   * @param {string} [db]
   * @param {string} [schema]
   * @param {string} [sp]
   * @return {Observable<any[]>}
   * @memberof EidosApiService
   */
  public getJSON<T extends { HasError: boolean }>(JSONName: string, parameters?: any, db?: string, schema?: string, sp?: string): Observable<any[]> {
    if (!parameters) {
      parameters = {} as any;
    }
    if (db) {
      parameters.db = db;
    }
    if (schema) {
      parameters.schema = schema;
    }
    if (sp) {
      parameters.sp = sp;
    }

    return this.sendCommand("POST", "json/get/" + JSONName, parameters).pipe(
      map<T, any>((response: T) => !response.HasError && (JSONName in response) ? (<any>response)[JSONName] : []),
      catchError((error: HttpErrorResponse) => this.handleGenericError(error))
    );
  }
}
